import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../Components/Navbar";
import { fetchProfile } from "../../utils/fetchProfile";
import Cookies from "js-cookie";
import { CircularProgress, Tabs, Tab, Tooltip, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { BootstrapButton } from "../SetupCatalogReport/components/BootstrapButton";
import ConfirmModal from "../SetupCatalogReport/components/ConfirmModal";
 
import {
  apiFetch, fetchAllPages, PRODUCT_PAGE_SIZE,
  toastSuccess, toastError,
  ANY, DRAFT_ID, emptyFilterForm, parseMulti, joinMulti, buildPayload,
  useEntitySearch, generateReportLineSQL,
} from "./CustomFilterHelpers";
import { AttributeTable, ProductsTable, RefreshModal } from "./CustomFilterTables";
 
const getToken = () => Cookies.get("access_token") || "";
 
export default function CustomFilter() {
  const navigate = useNavigate();
  const { catalogId, reportLineId } = useParams();
 
  const [dataProfile, setDataProfile] = useState([]);
  const [notAllowAccessPage, setNotAllowAccess] = useState(false);
 
  const [reportLine, setReportLine] = useState(null);
  const [loadingLine, setLoadingLine] = useState(false);
 
  const [filters, setFilters] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [activeFilterId, setActiveFilterId] = useState(null);
 
  const [form, setForm] = useState(emptyFilterForm);
  const [savingForm, setSavingForm] = useState(false);
 
  const [productAttributes, setProductAttributes] = useState([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
 
  const [copyingFilter, setCopyingFilter] = useState(false);
  const [deletingFilter, setDeletingFilter] = useState(false);
 
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const [refreshingGenerate, setRefreshingGenerate] = useState(false);
 
  const [loadingActiveDetail, setLoadingActiveDetail] = useState(false);
 
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productPage, setProductPage] = useState(1);
  const [productTotalPages, setProductTotalPages] = useState(1);
  const [productTotalItems, setProductTotalItems] = useState(0);
 
  const [cacheTick, setCacheTick] = useState(0);
 
  const categoryNameCache = useRef({});
  const principalNameCache = useRef({});
  const brandNameCache = useRef({});
  const productNameCache = useRef({});
 
  const draftDefaultTriedRef = useRef(false);
 
  const [confirm, setConfirm] = useState({
    open: false, title: "", message: "", warningText: "", infoText: "",
    onConfirm: null, confirmLabel: "Ya", confirmColor: null,
  });
  const closeConfirm = () => setConfirm((p) => ({ ...p, open: false }));
 
  useEffect(() => {
    const h = setTimeout(() => { fetchProfile(getToken(), setDataProfile, setNotAllowAccess, ["13", "14"]); }, 300);
    return () => clearTimeout(h);
  }, []);
 
  const fetchReportLineInfo = useCallback(async () => {
    setLoadingLine(true);
    try {
      const res = await apiFetch.get(`/api/v1/catalog-report/report-lines/${reportLineId}`);
      const line = res?.data || res;
      setReportLine(line);
    } catch (e) {
      toastError(e?.message || "Gagal memuat info baris");
      setReportLine({ report_line_id: reportLineId, reportingline_desc: "" });
    } finally {
      setLoadingLine(false);
    }
  }, [reportLineId]);
 
  const fetchFilters = useCallback(async (selectAfterId = null) => {
    setLoadingFilters(true);
    try {
      const data = await fetchAllPages(
        `/api/v1/catalog-report/report-line-filters`,
        `&report_line_id=${reportLineId}&sort_by=id&sort_order=asc`
      );
      setFilters(data);
      if (selectAfterId) {
        setActiveFilterId(selectAfterId);
      } else {
        setActiveFilterId((prev) => {
          if (prev && data.find((f) => f.id === prev)) return prev;
          return data[0]?.id || null;
        });
      }
    } catch {
      toastError("Gagal memuat daftar custom filter");
    } finally {
      setLoadingFilters(false);
      setFiltersLoaded(true);
    }
  }, [reportLineId]);
 
  useEffect(() => { fetchReportLineInfo(); }, [fetchReportLineInfo]);
  useEffect(() => { fetchFilters(); }, [fetchFilters]);
 
  const activeFilterIndex = filters.findIndex((f) => f.id === activeFilterId);
  const displayFilterNo = activeFilterIndex >= 0 ? activeFilterIndex + 1 : "";
 
  const activeFilter = filters.find((f) => f.id === activeFilterId) || null;
  const isFirstFilter = filters.length > 0 && filters[0].id === activeFilterId && filters[0].id !== DRAFT_ID;
  const isDraftActive = activeFilter?.id === DRAFT_ID;
  const noFilterYet = !activeFilter;
 
  const hasDraft = filters.some((f) => f.id === DRAFT_ID);
 
  const handleAddFilter = useCallback(() => {
    draftDefaultTriedRef.current = false;
    setFilters((prev) => {
      if (prev.some((f) => f.id === DRAFT_ID)) return prev;
      const draft = {
        id: DRAFT_ID,
        report_line_id: reportLineId,
        custom_filter_no: prev.length + 1,
        category_master_id: "",
        principal_master_id: "",
        brand_master_ids: "",
        product_codes: "",
        packsize: "",
        packtype: "",
        flavour: "",
        segment_1: "",
        segment_2: "",
        segment_3: "",
        report_line_sql: null,
      };
      return [...prev, draft];
    });
    setActiveFilterId(DRAFT_ID);
  }, [reportLineId]);
 
  useEffect(() => {
    if (filtersLoaded && !loadingFilters && filters.length === 0) {
      handleAddFilter();
    }
  }, [filtersLoaded, loadingFilters, filters.length, handleAddFilter]);
 
  const searchResetRef = useRef(() => {});
 
  useEffect(() => {
    searchResetRef.current();
 
    if (!activeFilter) { setForm(emptyFilterForm); return; }
 
    setForm({
      category_master_id: activeFilter.category_master_id || "",
      principal_master_id: activeFilter.principal_master_id || "",
      brand_master_ids: parseMulti(activeFilter.brand_master_ids),
      product_codes: parseMulti(activeFilter.product_codes),
      packsize: activeFilter.packsize || "",
      packtype: activeFilter.packtype || "",
      flavour: activeFilter.flavour || "",
      segment_1: activeFilter.segment_1 || "",
      segment_2: activeFilter.segment_2 || "",
      segment_3: activeFilter.segment_3 || "",
    });
 
    if (activeFilter.id === DRAFT_ID) return;
 
    let cancelled = false;
    (async () => {
      setLoadingActiveDetail(true);
      try {
        const detail = await apiFetch.get(`/api/v1/catalog-report/report-line-filters/${activeFilter.id}`);
        const d = detail?.data || detail;
        if (cancelled || !d) return;
 
        setForm({
          category_master_id: d.category_master_id || "",
          principal_master_id: d.principal_master_id || "",
          brand_master_ids: parseMulti(d.brand_master_ids),
          product_codes: parseMulti(d.product_codes),
          packsize: d.packsize || "",
          packtype: d.packtype || "",
          flavour: d.flavour || "",
          segment_1: d.segment_1 || "",
          segment_2: d.segment_2 || "",
          segment_3: d.segment_3 || "",
        });
 
        if (d.category_master_id && d.description_category) {
          categoryNameCache.current[String(d.category_master_id)] = d.description_category;
        }
        if (d.principal_master_id && d.description_principal) {
          principalNameCache.current[String(d.principal_master_id)] = d.description_principal;
        }
        (d.description_brands || []).forEach((b) => {
          if (b?.id) brandNameCache.current[String(b.id)] = b.name;
        });
        (d.description_products || []).forEach((p) => {
          if (p?.code) productNameCache.current[String(p.code)] = p.name;
        });
 
        setCacheTick((t) => t + 1);
      } catch {
      } finally {
        if (!cancelled) setLoadingActiveDetail(false);
      }
    })();
 
    return () => { cancelled = true; };
  }, [activeFilter]);
 
  const fetchDefaultCategoryForDraft = useCallback(async () => {
    if (!reportLine?.ord_id) return null;
    try {
      const res = await apiFetch.get(
        `/api/v1/catalog-report/reports/${catalogId}/category/${reportLine.ord_id}`
      );
      const cat = res?.data?.category_master;
      if (!cat?.id) return null;
      return { id: String(cat.id), name: cat.name || "" };
    } catch {
      return null;
    }
  }, [catalogId, reportLine?.ord_id]);
 
  useEffect(() => {
    if (!isDraftActive) return;
    if (form.category_master_id) return;
    if (draftDefaultTriedRef.current) return;
    if (!reportLine?.ord_id) return;
 
    draftDefaultTriedRef.current = true;
    let cancelled = false;
    (async () => {
      const def = await fetchDefaultCategoryForDraft();
      if (cancelled || !def) return;
      categoryNameCache.current[def.id] = def.name;
      setCacheTick((t) => t + 1);
      setForm((p) => (p.category_master_id ? p : { ...p, category_master_id: def.id }));
    })();
    return () => { cancelled = true; };
  }, [isDraftActive, form.category_master_id, reportLine?.ord_id, fetchDefaultCategoryForDraft]);
 
  const brandIdsKey = form.brand_master_ids.join(",");
  const productCodesKey = form.product_codes.join(",");
 
  const reportLineSqlPreview = useMemo(
    () => generateReportLineSQL(form),
    [
      form.category_master_id,
      form.principal_master_id,
      brandIdsKey,
      productCodesKey,
      form.packsize,
      form.packtype,
      form.flavour,
      form.segment_1,
      form.segment_2,
      form.segment_3,
    ]
  );
 
  useEffect(() => { setProductPage(1); }, [form.category_master_id, form.principal_master_id, brandIdsKey, productCodesKey, form.packsize, form.packtype, form.flavour, form.segment_1, form.segment_2, form.segment_3]);
  useEffect(() => {
    if (!activeFilter || !form.category_master_id) {
      setSelectedProducts([]);
      setProductTotalPages(1);
      setProductTotalItems(0);
      return;
    }
    let cancelled = false;
    const h = setTimeout(async () => {
      setLoadingProducts(true);
      try {
        let extra = `&category_master_id=${form.category_master_id}`;
        if (form.principal_master_id) extra += `&principal_master_id=${form.principal_master_id}`;
        if (brandIdsKey) extra += `&brand_master_id=${brandIdsKey}`;
        if (productCodesKey) extra += `&product_id=${productCodesKey}`;
        if (form.packsize) extra += `&packsize=${encodeURIComponent(form.packsize)}`;
        if (form.packtype) extra += `&packtype=${encodeURIComponent(form.packtype)}`;
        if (form.flavour) extra += `&flavour=${encodeURIComponent(form.flavour)}`;
        if (form.segment_1) extra += `&segment_1=${encodeURIComponent(form.segment_1)}`;
        if (form.segment_2) extra += `&segment_2=${encodeURIComponent(form.segment_2)}`;
        if (form.segment_3) extra += `&segment_3=${encodeURIComponent(form.segment_3)}`;
        const r = await apiFetch.get(`/api/v1/product-master?page=${productPage}&size=${PRODUCT_PAGE_SIZE}${extra}`);
        if (!cancelled) {
          setSelectedProducts(r?.data || []);
          setProductTotalPages(r?.pagination_info?.total_pages || 1);
          setProductTotalItems(r?.pagination_info?.total ?? (r?.data || []).length);
        }
      } catch (e) {
        if (!cancelled) {
          setSelectedProducts([]);
          setProductTotalPages(1);
          setProductTotalItems(0);
          if (productPage !== 1) {
            toastError("Gagal memuat halaman produk, kembali ke halaman 1");
            setProductPage(1);
          } else {
            toastError(e?.message || "Gagal memuat produk");
          }
        }
      } finally {
        if (!cancelled) setLoadingProducts(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(h); };
  }, [activeFilter, form.category_master_id, form.principal_master_id, brandIdsKey, productCodesKey, form.packsize, form.packtype, form.flavour, form.segment_1, form.segment_2, form.segment_3, productPage]);
 
  const [attributesTriggered, setAttributesTriggered] = useState(false);
  const triggerAttributes = useCallback(() => setAttributesTriggered(true), []);
 
  useEffect(() => {
    if (!form.category_master_id || !attributesTriggered) { setProductAttributes([]); return; }
    let cancelled = false;
    const h = setTimeout(async () => {
      setLoadingAttributes(true);
      try {
        const data = await apiFetch.get(`/api/v1/product-master-attribute?category_master_id=${form.category_master_id}`);
        if (!cancelled) setProductAttributes(data?.data || []);
      } catch {
        if (!cancelled) setProductAttributes([]);
      } finally {
        if (!cancelled) setLoadingAttributes(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(h); };
  }, [form.category_master_id, attributesTriggered]);
 
  const packsizeOptions = useMemo(
    () => productAttributes.filter((a) => a.atribut === "packsize").map((a) => a.atribut_value).filter(Boolean),
    [productAttributes]
  );
  const packtypeOptions = useMemo(
    () => productAttributes.filter((a) => a.atribut === "packtype").map((a) => a.atribut_value).filter(Boolean),
    [productAttributes]
  );
  const flavourOptions = useMemo(
    () => productAttributes.filter((a) => a.atribut === "flavour").map((a) => a.atribut_value).filter(Boolean),
    [productAttributes]
  );
  const segment_1Options = useMemo(
    () => productAttributes.filter((a) => a.atribut === "segment_1").map((a) => a.atribut_value).filter(Boolean),
    [productAttributes]
  );
  const segment_2Options = useMemo(
    () => productAttributes.filter((a) => a.atribut === "segment_2").map((a) => a.atribut_value).filter(Boolean),
    [productAttributes]
  );
  const segment_3Options = useMemo(
    () => productAttributes.filter((a) => a.atribut === "segment_3").map((a) => a.atribut_value).filter(Boolean),
    [productAttributes]
  );
 
  const categorySearch = useEntitySearch({
    basePath: "/api/v1/category-master",
    nameCacheRef: categoryNameCache,
    idKey: "id",
    nameKey: "name",
    enabled: true,
  });
 
  const categoryFilterQuery = useMemo(
    () => (form.category_master_id ? `&category_master_id=${form.category_master_id}` : ""),
    [form.category_master_id]
  );
 
  const principalSearch = useEntitySearch({
    basePath: "/api/v1/brand-category-master",
    extraQuery: categoryFilterQuery,
    nameCacheRef: principalNameCache,
    idKey: "principal_master_id",
    nameKey: "principal_master",
    dedupeById: true,
    enabled: !!form.category_master_id,
    lazy: true,
  });
 
  const brandSearch = useEntitySearch({
    basePath: "/api/v1/brand-category-master",
    extraQuery: categoryFilterQuery,
    nameCacheRef: brandNameCache,
    idKey: "brand_master_id",
    nameKey: "brand_master",
    dedupeById: true,
    enabled: !!form.category_master_id,
    lazy: true,
  });
 
  const productSearchExtraQuery = useMemo(() => {
    if (!form.category_master_id) return "";
    let extra = `&category_master_id=${form.category_master_id}`;
    if (form.principal_master_id) extra += `&principal_master_id=${form.principal_master_id}`;
    if (brandIdsKey) extra += `&brand_master_id=${brandIdsKey}`;
    return extra;
  }, [form.category_master_id, form.principal_master_id, brandIdsKey]);
 
  const productSearch = useEntitySearch({
    basePath: "/api/v1/product-master",
    extraQuery: productSearchExtraQuery,
    nameCacheRef: productNameCache,
    idKey: "product_id",
    nameKey: "product_name",
    enabled: !!form.category_master_id,
    lazy: true,
  });
 
  useEffect(() => {
    searchResetRef.current = () => {
      principalSearch.reset();
      brandSearch.reset();
      productSearch.reset();
    };
  }, [categorySearch.reset, principalSearch.reset, brandSearch.reset, productSearch.reset]);
 
  const categoryValue = useMemo(() => {
    if (!form.category_master_id) return null;
    return { id: form.category_master_id, name: categoryNameCache.current[String(form.category_master_id)] || "" };
  }, [form.category_master_id, cacheTick]);
 
  const principalValue = useMemo(() => {
    if (!form.principal_master_id) return ANY;
    return { id: form.principal_master_id, name: principalNameCache.current[String(form.principal_master_id)] || "" };
  }, [form.principal_master_id, cacheTick]);
 
  const brandValue = useMemo(
    () => form.brand_master_ids.map((id) => ({ id, name: brandNameCache.current[String(id)] || id })),
    [brandIdsKey, cacheTick]
  );
 
  const productValue = useMemo(
    () => form.product_codes.map((id) => ({ id, name: productNameCache.current[String(id)] || id })),
    [productCodesKey, cacheTick]
  );
 
  const handleCopyFilter = async () => {
    if (!activeFilter?.id || isDraftActive) return;
    setCopyingFilter(true);
    try {
      const detail = await apiFetch.get(`/api/v1/catalog-report/report-line-filters/${activeFilter.id}`);
      const src = detail?.data || detail;
      const r = await apiFetch.post(`/api/v1/catalog-report/report-line-filters?report_line_id=${reportLineId}`, {
        category_master_id: src?.category_master_id || "",
        principal_master_id: src?.principal_master_id || "",
        brand_master_ids: joinMulti(parseMulti(src?.brand_master_ids)),
        product_codes: joinMulti(parseMulti(src?.product_codes)),
        packsize: src?.packsize || "",
        packtype: src?.packtype || "",
        flavour: src?.flavour || "",
        segment_1: src?.segment_1 || "",
        segment_2: src?.segment_2 || "",
        segment_3: src?.segment_3 || "",
      });
      toastSuccess("Filter berhasil di-copy");
      await fetchFilters(r?.data?.id || r?.id);
    } catch (e) {
      toastError(e?.message || "Gagal meng-copy filter");
    } finally {
      setCopyingFilter(false);
    }
  };
 
  const handleDeleteFilter = () => {
    if (!activeFilter || isFirstFilter) return;
 
    if (isDraftActive) {
      setFilters((prev) => prev.filter((f) => f.id !== DRAFT_ID));
      setActiveFilterId((prev) => {
        const rest = filters.filter((f) => f.id !== DRAFT_ID);
        return rest[0]?.id || null;
      });
      return;
    }
 
    setConfirm({
      open: true,
      title: "Hapus Custom Filter?",
      message: `Custom Filter No ${displayFilterNo} akan dihapus permanen.`,
      warningText: "Tindakan ini tidak dapat dibatalkan.",
      infoText: "",
      confirmLabel: "Ya, Hapus",
      confirmColor: "#e53935",
      onConfirm: async () => {
        closeConfirm();
        setDeletingFilter(true);
        try {
          await apiFetch.delete(`/api/v1/catalog-report/report-line-filters/${activeFilter.id}`);
          toastSuccess("Custom filter dihapus");
          await fetchFilters();
        } catch (e) {
          toastError(e?.message || "Gagal menghapus custom filter");
        } finally {
          setDeletingFilter(false);
        }
      },
    });
  };
 
  const openRefreshModal = () => {
    if (isDraftActive) return toastError("Simpan filter ini dulu sebelum refresh");
    setRefreshModalOpen(true);
  };
  const closeRefreshModal = () => {
    if (refreshingGenerate) return;
    setRefreshModalOpen(false);
  };
 
  const handleConfirmRefreshFilter = async () => {
    setRefreshingGenerate(true);
    try {
      await apiFetch.post(
        `/api/v1/catalog-report/${catalogId}/catalog/custom/generate?delete_existing=true&report_line_id=${reportLineId}`,
        {}
      );
      toastSuccess("Produk berhasil di-generate ulang");
      setRefreshModalOpen(false);
    } catch (e) {
      toastError(e?.message || "Gagal generate ulang produk");
    } finally {
      setRefreshingGenerate(false);
    }
  };
 
  const handleSaveForm = async () => {
    if (!activeFilter) return;
    if (!form.category_master_id) return toastError("Category wajib dipilih");
    setSavingForm(true);
    try {
      const payload = buildPayload(form);
      if (isDraftActive) {
        const r = await apiFetch.post(`/api/v1/catalog-report/report-line-filters?report_line_id=${reportLineId}`, payload);
        toastSuccess("Custom filter berhasil dibuat");
        await fetchFilters(r?.data?.id || r?.id);
      } else {
        await apiFetch.put(`/api/v1/catalog-report/report-line-filters/${activeFilter.id}`, payload);
        toastSuccess("Perubahan filter berhasil disimpan");
        await fetchFilters(activeFilter.id);
      }
    } catch (e) {
      toastError(e?.message || "Gagal menyimpan filter");
    } finally {
      setSavingForm(false);
    }
  };
 
  if (notAllowAccessPage) return (
    <div>
      <Navbar active="Stat Module" initial={dataProfile.initial} role_code={dataProfile.role_code} />
      <div style={{ padding: 24 }}>Anda tidak memiliki akses ke halaman ini.</div>
    </div>
  );
 
  return (
    <div>
      <Navbar active="Stat Module" initial={dataProfile.initial} role_code={dataProfile.role_code} />
      <div style={{ padding: "1rem" }}>
 
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <Tabs
            value={activeFilterId || false}
            onChange={(_, v) => setActiveFilterId(v)}
            sx={{
              minHeight: 40,
              "& .MuiTabs-flexContainer": { gap: 8 },
              "& .MuiTab-root": {
                minHeight: 40, textTransform: "none", fontWeight: 700, fontSize: 15,
                border: "1.5px solid #ddd", borderRadius: "8px 8px 0 0",
                "&.Mui-selected": { borderColor: "#000", color: "#000" },
              },
              "& .MuiTabs-indicator": { display: "none" },
            }}
          >
            {filters.length === 0 && loadingFilters ? (
              <Tab disabled label={<CircularProgress size={16} />} />
            ) : filters.length === 0 ? (
              <Tab disabled label="Belum ada custom filter" />
            ) : filters.map((f, idx) => (
              <Tab
                key={f.id}
                value={f.id}
                label={`Custom Filter ${idx + 1}${f.id === DRAFT_ID ? " (draft)" : ""}`}
                sx={f.id === DRAFT_ID ? { fontStyle: "italic", color: "#f57c00 !important" } : {}}
              />
            ))}
          </Tabs>
 
          <BootstrapButton
            disableRipple
            disabled={hasDraft}
            onClick={handleAddFilter}
            sx={{ py: 0.7, px: 2, fontSize: 13, minWidth: "unset", whiteSpace: "nowrap", color: "#fff" }}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          >
            Add Filter
          </BootstrapButton>
 
          {loadingFilters && filters.length > 0 && (
            <CircularProgress size={16} sx={{ ml: 0.5 }} />
          )}
 
          <div style={{ flex: 1 }} />
 
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate(`/stat-module/setup-catalog-report/${catalogId}`)}
            sx={{ textTransform: "none", fontSize: 12.5, borderColor: "#000", color: "#333", whiteSpace: "nowrap" }}
          >
            {"<= Go To Reportingline"}
          </Button>
        </div>
 
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
          <AttributeTable
            reportLineId={reportLineId}
            reportLine={reportLine}
            loadingLine={loadingLine}
            activeFilter={activeFilter}
            reportLineSqlPreview={reportLineSqlPreview}
            displayFilterNo={displayFilterNo}
            isDraftActive={isDraftActive}
            loadingActiveDetail={loadingActiveDetail}
            disabled={noFilterYet}
            form={form}
            setForm={setForm}
            categorySearch={categorySearch}
            principalSearch={principalSearch}
            brandSearch={brandSearch}
            productSearch={productSearch}
            categoryValue={categoryValue}
            principalValue={principalValue}
            brandValue={brandValue}
            productValue={productValue}
            packsizeOptions={packsizeOptions}
            packtypeOptions={packtypeOptions}
            flavourOptions={flavourOptions}
            segment_1Options={segment_1Options}
            segment_2Options={segment_2Options}
            segment_3Options={segment_3Options}
            loadingAttributes={loadingAttributes}
            triggerAttributes={triggerAttributes}
            savingForm={savingForm}
            onSave={handleSaveForm}
          />
 
          {!noFilterYet && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 220 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Tooltip title={isDraftActive ? "Simpan filter dulu sebelum refresh" : ""}>
                  <span>
                    <BootstrapButton
                      disableRipple
                      disabled={isDraftActive}
                      onClick={openRefreshModal}
                      sx={{ py: 0.7, px: 2, fontSize: 13, color: "#fff", whiteSpace: "nowrap" }}
                      startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
                    >
                      Refresh Filter
                    </BootstrapButton>
                  </span>
                </Tooltip>
 
                <Tooltip title={isDraftActive ? "Simpan filter dulu sebelum copy" : ""}>
                  <span>
                    <BootstrapButton
                      disableRipple
                      disabled={copyingFilter || isDraftActive}
                      onClick={handleCopyFilter}
                      sx={{ py: 0.7, px: 2, fontSize: 13, color: "#fff", whiteSpace: "nowrap" }}
                    >
                      {copyingFilter ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Copy Filter"}
                    </BootstrapButton>
                  </span>
                </Tooltip>
 
                <Tooltip title={isFirstFilter ? "Custom Filter 1 tidak dapat dihapus" : ""}>
                  <span>
                    <Button
                      variant="contained"
                      disableElevation
                      disableRipple
                      disabled={isFirstFilter || deletingFilter}
                      onClick={handleDeleteFilter}
                      sx={{
                        py: 0.7, px: 2, fontSize: 13, textTransform: "none", whiteSpace: "nowrap",
                        backgroundColor: "#c62828", color: "#fff",
                        "&:hover": { backgroundColor: "#b71c1c" },
                        "&.Mui-disabled": { backgroundColor: "#e5e7eb", color: "#9ca3af" },
                      }}
                    >
                      {deletingFilter ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : (isDraftActive ? "Batalkan Draft" : "Delete This Filter")}
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </div>
          )}
        </div>
 
        <ProductsTable
          disabled={noFilterYet}
          isDraftActive={isDraftActive}
          displayFilterNo={displayFilterNo}
          productTotalItems={productTotalItems}
          loadingProducts={loadingProducts}
          hasCategory={!!form.category_master_id}
          selectedProducts={selectedProducts}
          productPage={productPage}
          productTotalPages={productTotalPages}
          setProductPage={setProductPage}
        />
      </div>
 
      <RefreshModal
        open={refreshModalOpen}
        onClose={closeRefreshModal}
        loading={refreshingGenerate}
        onConfirm={handleConfirmRefreshFilter}
      />
 
      <ConfirmModal
        open={confirm.open} title={confirm.title} message={confirm.message}
        warningText={confirm.warningText} infoText={confirm.infoText}
        onConfirm={confirm.onConfirm} onCancel={closeConfirm}
        loading={deletingFilter} confirmLabel={confirm.confirmLabel} confirmColor={confirm.confirmColor}
      />
    </div>
  );
}